import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import Peer from 'simple-peer';
import io from 'socket.io-client';

const socket = io('http://localhost:5000');

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background-color: #23272a;
  color: white;
`;

const Button = styled.button`
  margin: 10px;
  padding: 10px 20px;
  background-color: #7289da;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;

  &:hover {
    background-color: #5b6eae;
  }
`;

const VideoContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 20px;
`;

const Video = styled.video`
  width: 300px;
  height: 200px;
  margin: 10px;
  background: black;
`;

const ControlContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 10px;
`;

const ControlButton = styled.button`
  background-color: ${props => (props.active ? '#5b6eae' : '#7289da')};
  margin: 5px;
  padding: 10px;
  border-radius: 5px;
  cursor: pointer;
`;

function MainPage() {
  const [stream, setStream] = useState(null);
  const [receivingCall, setReceivingCall] = useState(false);
  const [callerSignal, setCallerSignal] = useState(null);
  const [callAccepted, setCallAccepted] = useState(false);
  const [peer, setPeer] = useState(null);
  const [micOn, setMicOn] = useState(true);
  const [cameraOn, setCameraOn] = useState(true);
  const [screenSharing, setScreenSharing] = useState(false);
  const userVideo = useRef();
  const partnerVideo = useRef();
  const connectionRef = useRef();

  useEffect(() => {
    socket.on('incoming-call', ({ from, offer }) => {
      setReceivingCall(true);
      setCallerSignal(offer);
    });

    socket.on('call-answered', ({ answer }) => {
      peer.signal(answer);
    });

    socket.on('ice-candidate', ({ candidate }) => {
      peer.addIceCandidate(new RTCIceCandidate(candidate));
    });

    socket.on('screen-shared', ({ from }) => {
      // Handle screen sharing
    });
  }, [peer]);

  // Medya cihazını alırken hata yakalama
  const getMedia = async (constraints) => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      return mediaStream;
    } catch (err) {
      console.error('Medya cihazı kullanılamıyor:', err);
      alert('Bir cihaz zaten kullanımda. Lütfen başka bir sekmede veya uygulamada kullanılmadığından emin olun.');
      throw err;
    }
  };

  // Mikrofonu açma/kapama
  const toggleMic = () => {
    const track = stream?.getTracks().find(track => track.kind === 'audio');
    if (track) {
      track.enabled = !micOn;
      setMicOn(!micOn);
    }
  };

  // Kamerayı açma/kapama
  const toggleCamera = () => {
    const track = stream?.getTracks().find(track => track.kind === 'video');
    if (track) {
      track.enabled = !cameraOn;
      setCameraOn(!cameraOn);
    }
  };

  // Ekran paylaşımını başlatma/sonlandırma
  const toggleScreenSharing = () => {
    if (screenSharing) {
      // Ekran paylaşımını sonlandırma
      const track = stream?.getTracks().find(track => track.kind === 'video');
      track?.stop(); // Paylaşılan ekran videosunu durdur
      setScreenSharing(false);
    } else {
      // Ekran paylaşımını başlatma
      navigator.mediaDevices.getDisplayMedia({ video: true }).then(screenStream => {
        const track = screenStream.getTracks()[0];
        stream.addTrack(track); // Ekran paylaşımlı akış ekle
        setScreenSharing(true);
      }).catch(err => {
        console.error('Ekran paylaşımı başlatılamadı:', err);
        alert('Ekran paylaşımı başlatılamadı. Lütfen izin verin.');
      });
    }
  };

  // Arama başlatma
  const startCall = async () => {
    try {
      const mediaStream = await getMedia({ video: true, audio: true });
      setStream(mediaStream);
      if (userVideo.current) userVideo.current.srcObject = mediaStream;

      const peer = new Peer({
        initiator: true,
        trickle: false,
        stream: mediaStream,
      });

      peer.on('signal', (data) => {
        socket.emit('call-user', { to: 'target-username', offer: data });
      });

      peer.on('stream', (stream) => {
        if (partnerVideo.current) partnerVideo.current.srcObject = stream;
      });

      peer.on('ice-candidate', (candidate) => {
        socket.emit('ice-candidate', { to: 'target-username', candidate });
      });

      setPeer(peer);
      connectionRef.current = peer;
    } catch (err) {
      console.error('Arama başlatılamadı:', err);
    }
  };

  // Aramayı kabul etme
  const answerCall = async () => {
    try {
      setCallAccepted(true);
      const mediaStream = await getMedia({ video: true, audio: true });
      setStream(mediaStream);
      if (userVideo.current) userVideo.current.srcObject = mediaStream;

      const peer = new Peer({
        initiator: false,
        trickle: false,
        stream: mediaStream,
      });

      peer.on('signal', (data) => {
        socket.emit('answer-call', { to: 'caller-username', answer: data });
      });

      peer.on('stream', (stream) => {
        if (partnerVideo.current) partnerVideo.current.srcObject = stream;
      });

      peer.on('ice-candidate', (candidate) => {
        socket.emit('ice-candidate', { to: 'caller-username', candidate });
      });

      peer.signal(callerSignal);
      setPeer(peer);
      connectionRef.current = peer;
    } catch (err) {
      console.error('Arama kabul edilemedi:', err);
    }
  };

  // Aramayı sonlandırma
  const endCall = () => {
    connectionRef.current.destroy();
    setCallAccepted(false);
    setReceivingCall(false);
    setPeer(null);
  };

  return (
    <Container>
      {!callAccepted ? (
        <div>
          <h1>Arama Yap</h1>
          <Button onClick={startCall}>Arama Başlat</Button>
          {receivingCall && (
            <div>
              <h2>Arama Geliyor...</h2>
              <Button onClick={answerCall}>Kabul Et</Button>
              <Button onClick={endCall}>Reddet</Button>
            </div>
          )}
        </div>
      ) : (
        <div>
          <h1>Arama Aktif</h1>
          <Button onClick={endCall}>Aramayı Sonlandır</Button>
        </div>
      )}

      <ControlContainer>
        <ControlButton onClick={toggleMic} active={micOn}>
          {micOn ? 'Mikrofonu Kapat' : 'Mikrofonu Aç'}
        </ControlButton>
        <ControlButton onClick={toggleCamera} active={cameraOn}>
          {cameraOn ? 'Kamerayı Kapat' : 'Kamerayı Aç'}
        </ControlButton>
        <ControlButton onClick={toggleScreenSharing} active={screenSharing}>
          {screenSharing ? 'Ekran Paylaşımını Durdur' : 'Ekran Paylaşımı Başlat'}
        </ControlButton>
      </ControlContainer>

      <VideoContainer>
        <Video ref={userVideo} autoPlay playsInline muted />
        <Video ref={partnerVideo} autoPlay playsInline />
      </VideoContainer>
    </Container>
  );
}

export default MainPage;